import { getVisibleExtraActivities } from 'utils/assigmentsGenerator';
import { formatTime, sortByDateUsingTimestamp } from 'utils/datetime';

import { ASSIGNMENT_DETAILS_SIDEBAR_TEXT } from '../text';

import styles from './styles.module.css';

const applyWorkAssignmentTimestamps = (stations, workAssignments) => {
	if (!workAssignments?.length) return stations;

	return stations.map((station, index) => {
		const arrival = index > 0 ? workAssignments[index - 1]?.toTimestamp : null;
		const plannedDeparture = workAssignments[index]?.fromTimestamp ?? null;
		return { ...station, arrival, plannedDeparture };
	});
};

// Sort stations and extra activities by chronological order
export const buildRouteData = (stations, extraActivities, workAssignments) => {
	const visibleExtras = getVisibleExtraActivities(extraActivities);
	const stationsWithTimes = applyWorkAssignmentTimestamps(stations, workAssignments);
	const lastStationId = stationsWithTimes[stationsWithTimes.length - 1]?.id;

	const extrasByStation = {};

	for (const extra of visibleExtras) {
		const stationId = extra.fromStation?.id;
		if (!stationId) continue;

		if (!extrasByStation[stationId]) {
			extrasByStation[stationId] = [];
		}
		extrasByStation[stationId].push(extra);
	}

	Object.values(extrasByStation).forEach((extras) => {
		extras.sort((a, b) => sortByDateUsingTimestamp(a.fromTimestamp, b.fromTimestamp));
	});

	const timeline = [];

	for (const station of stationsWithTimes) {
		const extras = extrasByStation[station.id];
		const isLastOnly = station.id === lastStationId && station.id !== stationsWithTimes[0]?.id;

		if (extras && !isLastOnly) {
			for (const extra of extras) {
				timeline.push({ type: 'extra', data: extra });
			}
		}

		timeline.push({ type: 'station', data: station });

		if (extras && isLastOnly) {
			for (const extra of extras) {
				timeline.push({ type: 'extra', data: extra });
			}
		}
	}

	return timeline;
};

const createTimeElementProps = (label, dateTime) => ({
	className: styles.bold,
	label,
	Elem: 'ins',
	dateTime,
	children: dateTime,
});

export const createTimeProps = (station, timezoneToUse, isShort) => {
	const timeProps = {};
	if (!station.arrival && !station.plannedDeparture) return timeProps;

	const firstTime = station.arrival ? station.arrival : station.plannedDeparture;
	const secondTime = station.plannedDeparture && station.arrival ? station.plannedDeparture : '';
	const timezone = timezoneToUse || station.timezone;
	const formattedFirstTime = formatTime(firstTime, { timezone });

	timeProps.time = createTimeElementProps(
		ASSIGNMENT_DETAILS_SIDEBAR_TEXT.arrival,
		formattedFirstTime
	);

	if (secondTime && !isShort) {
		const formattedSecondTime = formatTime(station.plannedDeparture, { timezone });
		timeProps.secondTime = createTimeElementProps(
			ASSIGNMENT_DETAILS_SIDEBAR_TEXT.departure,
			formattedSecondTime
		);
	}
	return timeProps;
};

export const createExtraTimeProps = (timestamp, timezoneToUse) => {
	const formatted = formatTime(timestamp, { timezone: timezoneToUse });
	return createTimeElementProps('Activity time:', formatted);
};

export const getExtraActivityKey = (extraActivity, index) =>
	extraActivity.id ??
	[
		extraActivity.activityType,
		extraActivity.fromStation?.id ?? extraActivity.fromStation?.code,
		extraActivity.toStation?.id ?? extraActivity.toStation?.code,
		extraActivity.fromTimestamp,
		extraActivity.toTimestamp,
		index,
	].join('-');

export const getStationKey = (station, index) => station.id ?? `${station.code}-${index}`;
